import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LoanRepoManager = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('loans');
  const [status, setStatus] = useState(null);
  const [loans, setLoans] = useState({ database_loans: [], onchain_loans: [] });
  const [repos, setRepos] = useState({ database_repos: [], onchain_repos: [] });
  const [pendingRequests, setPendingRequests] = useState({ pending_loans: [], pending_repos: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showRepoModal, setShowRepoModal] = useState(false);
  
  // Form states
  const [loanForm, setLoanForm] = useState({
    borrower_wallet: '',
    amount: '',
    collateral: '',
    interest_rate: '8',
    duration_days: '90',
    purpose: ''
  });
  
  const [repoForm, setRepoForm] = useState({
    buyer_wallet: '',
    amount: '',
    repo_rate: '6',
    duration_days: '30',
    purpose: ''
  });

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch status
      const statusRes = await fetch(`${API}/api/loan-repo/status`);
      const statusData = await statusRes.json();
      setStatus(statusData);
      
      // Fetch loans
      const loansRes = await fetch(`${API}/api/loan-repo/loans`);
      const loansData = await loansRes.json();
      setLoans(loansData);
      
      // Fetch repos
      const reposRes = await fetch(`${API}/api/loan-repo/repos`);
      const reposData = await reposRes.json();
      setRepos(reposData);
      
      // Fetch stats and pending if admin
      if (isAdmin && token) {
        const statsRes = await fetch(`${API}/api/loan-repo/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statsData = await statsRes.json();
        setStats(statsData);
        
        const pendingRes = await fetch(`${API}/api/loan-repo/admin/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const pendingData = await pendingRes.json();
        setPendingRequests(pendingData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitLoanRequest = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/loan-repo/loans/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...loanForm,
          amount: parseFloat(loanForm.amount),
          collateral: parseFloat(loanForm.collateral),
          interest_rate: parseFloat(loanForm.interest_rate),
          duration_days: parseInt(loanForm.duration_days)
        })
      });
      
      const data = await res.json();
      if (data.success) {
        alert(`Loan request submitted! ID: ${data.loan_request_id}`);
        setShowLoanModal(false);
        setLoanForm({
          borrower_wallet: '',
          amount: '',
          collateral: '',
          interest_rate: '8',
          duration_days: '90',
          purpose: ''
        });
        fetchData();
      } else {
        alert(data.detail || 'Failed to submit loan request');
      }
    } catch (err) {
      console.error('Error submitting loan:', err);
      alert('Error submitting loan request');
    }
  };

  const submitRepoRequest = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/loan-repo/repos/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...repoForm,
          amount: parseFloat(repoForm.amount),
          repo_rate: parseFloat(repoForm.repo_rate),
          duration_days: parseInt(repoForm.duration_days)
        })
      });
      
      const data = await res.json();
      if (data.success) {
        alert(`Repo request submitted! ID: ${data.repo_request_id}`);
        setShowRepoModal(false);
        setRepoForm({
          buyer_wallet: '',
          amount: '',
          repo_rate: '6',
          duration_days: '30',
          purpose: ''
        });
        fetchData();
      } else {
        alert(data.detail || 'Failed to submit repo request');
      }
    } catch (err) {
      console.error('Error submitting repo:', err);
      alert('Error submitting repo request');
    }
  };

  const approveLoan = async (loanRequestId) => {
    if (!window.confirm('Approve this loan request?')) return;
    
    try {
      const res = await fetch(`${API}/api/loan-repo/loans/${loanRequestId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        alert(`Loan approved! ${data.tx_hash ? `TX: ${data.tx_hash}` : ''}`);
        fetchData();
      } else {
        alert(data.detail || 'Failed to approve loan');
      }
    } catch (err) {
      console.error('Error approving loan:', err);
      alert('Error approving loan');
    }
  };

  const rejectLoan = async (loanRequestId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    
    try {
      const res = await fetch(`${API}/api/loan-repo/loans/${loanRequestId}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        alert('Loan rejected');
        fetchData();
      }
    } catch (err) {
      console.error('Error rejecting loan:', err);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending_review: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      approved_pending_chain: 'bg-blue-500/20 text-blue-400',
      active: 'bg-emerald-500/20 text-emerald-400',
      repaid: 'bg-gray-500/20 text-gray-400',
      executed: 'bg-gray-500/20 text-gray-400',
      rejected: 'bg-red-500/20 text-red-400',
      defaulted: 'bg-red-500/20 text-red-400'
    };
    return `px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-gray-500/20 text-gray-400'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="loan-repo-page">
      {/* Header */}
      <header className="bg-[#0d1421] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              ← Back
            </button>
            <h1 className="text-xl font-semibold">Securities Lending & Repo</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLoanModal(true)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm font-medium"
              data-testid="new-loan-btn"
            >
              + New Loan
            </button>
            <button
              onClick={() => setShowRepoModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium"
              data-testid="new-repo-btn"
            >
              + New Repo
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* System Status */}
        <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-2">System Status</h2>
              <div className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${status?.status === 'active' ? 'bg-green-500' : status?.status === 'pending_deployment' ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
                <span className="text-gray-400">
                  {status?.status === 'active' ? 'Contract Active on Mainnet' : 
                   status?.status === 'pending_deployment' ? 'Awaiting Contract Deployment' : 
                   'System Unavailable'}
                </span>
              </div>
            </div>
            {status?.contract_address && (
              <a
                href={status.etherscan_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 text-sm"
              >
                View Contract ↗
              </a>
            )}
          </div>
          
          {status?.stats && (
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="bg-[#151d2e] rounded-lg p-4">
                <div className="text-2xl font-bold text-amber-400">{status.stats.total_loans}</div>
                <div className="text-gray-400 text-sm">Total Loans</div>
              </div>
              <div className="bg-[#151d2e] rounded-lg p-4">
                <div className="text-2xl font-bold text-green-400">{status.stats.active_loans}</div>
                <div className="text-gray-400 text-sm">Active Loans</div>
              </div>
              <div className="bg-[#151d2e] rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-400">{status.stats.total_repos}</div>
                <div className="text-gray-400 text-sm">Total Repos</div>
              </div>
              <div className="bg-[#151d2e] rounded-lg p-4">
                <div className="text-2xl font-bold text-cyan-400">{status.stats.active_repos}</div>
                <div className="text-gray-400 text-sm">Active Repos</div>
              </div>
            </div>
          )}
        </div>

        {/* Admin Pending Requests */}
        {isAdmin && pendingRequests.total_pending > 0 && (
          <div className="bg-[#0d1421] rounded-xl border border-yellow-800 p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              Pending Approval ({pendingRequests.total_pending})
            </h2>
            
            {pendingRequests.pending_loans.map(loan => (
              <div key={loan.loan_request_id} className="bg-[#151d2e] rounded-lg p-4 mb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{loan.loan_request_id}</div>
                    <div className="text-sm text-gray-400 mt-1">
                      {formatNumber(loan.amount_beth)} BETH @ {loan.interest_rate}% APR for {loan.duration_days} days
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Collateral: {formatNumber(loan.collateral_beth)} BETH (LTV: {loan.ltv_ratio}%)
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveLoan(loan.loan_request_id)}
                      className="px-3 py-1 bg-green-600 hover:bg-green-500 rounded text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => rejectLoan(loan.loan_request_id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-sm"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('loans')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'loans' ? 'bg-amber-600 text-white' : 'bg-[#151d2e] text-gray-400 hover:text-white'}`}
          >
            Loans ({loans.database_count || 0})
          </button>
          <button
            onClick={() => setActiveTab('repos')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'repos' ? 'bg-blue-600 text-white' : 'bg-[#151d2e] text-gray-400 hover:text-white'}`}
          >
            Repos ({repos.database_count || 0})
          </button>
        </div>

        {/* Loans Table */}
        {activeTab === 'loans' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Collateral</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Rate</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Duration</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Created</th>
                </tr>
              </thead>
              <tbody>
                {loans.database_loans?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No loan agreements yet
                    </td>
                  </tr>
                ) : (
                  loans.database_loans?.map(loan => (
                    <tr key={loan.loan_request_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-mono">{loan.loan_request_id}</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(loan.amount_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(loan.collateral_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{loan.interest_rate}%</td>
                      <td className="px-4 py-3 text-sm">{loan.duration_days} days</td>
                      <td className="px-4 py-3">
                        <span className={getStatusBadge(loan.status)}>{loan.status.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(loan.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Repos Table */}
        {activeTab === 'repos' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Repo Rate</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Repurchase</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Duration</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Created</th>
                </tr>
              </thead>
              <tbody>
                {repos.database_repos?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No repo agreements yet
                    </td>
                  </tr>
                ) : (
                  repos.database_repos?.map(repo => (
                    <tr key={repo.repo_request_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-mono">{repo.repo_request_id}</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(repo.amount_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{repo.repo_rate}%</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(repo.repurchase_price_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{repo.duration_days} days</td>
                      <td className="px-4 py-3">
                        <span className={getStatusBadge(repo.status)}>{repo.status.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(repo.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Loan Request Modal */}
      {showLoanModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Request Securities Loan</h2>
            <form onSubmit={submitLoanRequest}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Borrower Wallet Address</label>
                  <input
                    type="text"
                    value={loanForm.borrower_wallet}
                    onChange={(e) => setLoanForm({...loanForm, borrower_wallet: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                    placeholder="0x..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Loan Amount (BETH)</label>
                    <input
                      type="number"
                      value={loanForm.amount}
                      onChange={(e) => setLoanForm({...loanForm, amount: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                      placeholder="1000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Collateral (BETH)</label>
                    <input
                      type="number"
                      value={loanForm.collateral}
                      onChange={(e) => setLoanForm({...loanForm, collateral: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                      placeholder="1500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Interest Rate (%)</label>
                    <input
                      type="number"
                      value={loanForm.interest_rate}
                      onChange={(e) => setLoanForm({...loanForm, interest_rate: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                      step="0.1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Duration (Days)</label>
                    <select
                      value={loanForm.duration_days}
                      onChange={(e) => setLoanForm({...loanForm, duration_days: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                    >
                      <option value="30">30 Days</option>
                      <option value="60">60 Days</option>
                      <option value="90">90 Days</option>
                      <option value="180">180 Days</option>
                      <option value="365">365 Days</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Purpose (Optional)</label>
                  <textarea
                    value={loanForm.purpose}
                    onChange={(e) => setLoanForm({...loanForm, purpose: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowLoanModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg font-medium"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Repo Request Modal */}
      {showRepoModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Request Repo Agreement</h2>
            <form onSubmit={submitRepoRequest}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Buyer Wallet Address</label>
                  <input
                    type="text"
                    value={repoForm.buyer_wallet}
                    onChange={(e) => setRepoForm({...repoForm, buyer_wallet: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                    placeholder="0x..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Amount (BETH)</label>
                    <input
                      type="number"
                      value={repoForm.amount}
                      onChange={(e) => setRepoForm({...repoForm, amount: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                      placeholder="10000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Repo Rate (%)</label>
                    <input
                      type="number"
                      value={repoForm.repo_rate}
                      onChange={(e) => setRepoForm({...repoForm, repo_rate: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                      step="0.1"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Duration (Days)</label>
                  <select
                    value={repoForm.duration_days}
                    onChange={(e) => setRepoForm({...repoForm, duration_days: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="7">7 Days (Overnight)</option>
                    <option value="14">14 Days</option>
                    <option value="30">30 Days (Term)</option>
                    <option value="60">60 Days</option>
                    <option value="90">90 Days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Purpose (Optional)</label>
                  <textarea
                    value={repoForm.purpose}
                    onChange={(e) => setRepoForm({...repoForm, purpose: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowRepoModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanRepoManager;
